/**
 * What's New — constants, types and defaults for `configuration/whatsNew`
 * (RTDB). Pure: no Firebase import, so schemas and tests can use it freely.
 * The reads and the seed live in `whatsNewConfig.ts`.
 *
 * Design memo: Confluence "What's New — newly added brands & references (RN)"
 * (WK 2287861761), §3.1. The RN app reads this node live through its config
 * store; the admin edits it in the schema-driven panel (`whatsNewSchema`) and
 * the Collections editor reads `title` to lock the What's New card's name.
 *
 * The banner's `startsAt` / `endsAt` are calendar dates (`YYYY-MM-DD`), not
 * timestamps: the schedule is "from this day, through that day", and the RN
 * side decides the day boundary. Both are optional; an absent bound is open.
 */
export const WHATS_NEW_PATH = '/configuration/whatsNew';

/** Storage prefix for the configured banner image. Needs a watchlock storage.rules block. */
export const WHATS_NEW_BANNER_STORAGE_PREFIX = 'admin/whatsNewBanner';

export const WHATS_NEW_DEFAULT_TITLE = "What's New";

export type WhatsNewVisibility = 'admins' | 'everyone';
export type WhatsNewAutoBanner = 'off' | 'hero' | 'hero+fallback';

export interface WhatsNewBannerConfig {
  enabled: boolean;
  imageUrl?: string;
  storagePath?: string;
  caption?: string;
  subcaption?: string;
  ctaLabel?: string;
  ctaLink?: string;
  /** `YYYY-MM-DD`, first day shown. */
  startsAt?: string;
  /** `YYYY-MM-DD`, last day shown. */
  endsAt?: string;
  creditUrl?: string;
}

export interface WhatsNewConfig {
  enabled: boolean;
  visibility: WhatsNewVisibility;
  title: string;
  windowDays: number;
  enrichedRefsOnly: boolean;
  requireRefImage: boolean;
  autoBanner: WhatsNewAutoBanner;
  banner?: WhatsNewBannerConfig;
}

/** Memo §3.1 defaults: off at launch (Q1), admin-only preview (Q2). No banner. */
export const DEFAULT_WHATS_NEW_CONFIG: WhatsNewConfig = {
  enabled: false,
  visibility: 'admins',
  title: WHATS_NEW_DEFAULT_TITLE,
  windowDays: 7,
  enrichedRefsOnly: false,
  requireRefImage: true,
  autoBanner: 'hero+fallback',
};

/** The configured title, or the default when the node or key is absent or blank. */
export function resolveWhatsNewTitle(raw: unknown): string {
  return typeof raw === 'string' && raw.trim() ? raw.trim() : WHATS_NEW_DEFAULT_TITLE;
}

